import ConfirmInputNumber from 'components/ConfirmInput/ConfirmInputNumber'
import Donut from 'components/UI/Donut'
import { colors } from 'constants/colors'
import { useI18n } from 'context/I18nProvider'
import { useState } from 'react'

export type TrainingWishProvision = {
  id: number
  current_provisions: number
  initial_provisions: number
  manager: {
    id: number
    last_name: string
    first_name: string
    reference: string
    full_name: string
  }
  training: {
    id: number
    fictive: boolean
  }
  user: {
    id: number
  }
}

interface CellProps {
  title: string
  children: React.ReactNode
}

interface DistributionProps {
  trainingWish: TrainingWishProvision
  nbUsers: number
  onInitialChange: (initial: number) => Promise<void>
}

function Cell({ title, children }: CellProps) {
  return (
    <div className="flex flex-col text-center">
      <div className="font-bold mb-4">{title}</div>
      {children}
    </div>
  )
}

function Distribution({ trainingWish, nbUsers, onInitialChange }: DistributionProps) {
  const { i18n } = useI18n()
  const [initial, setInitial] = useState(trainingWish.initial_provisions)

  const assigned = trainingWish.initial_provisions - trainingWish.current_provisions + nbUsers
  const remaining = initial - assigned

  const cancel = () => {
    setInitial(trainingWish.initial_provisions)
  }

  const donut = [
    {
      color: colors.green,
      title: i18n('assign_provision_assigned'),
      value: assigned > initial ? initial : assigned,
    },
    {
      color: colors.pink,
      title: i18n('assign_provision_remaining'),
      value: remaining < 0 ? 0 : remaining,
    },
  ]
  const percent = Math.min(Math.ceil((assigned / initial) * 100), 100)
  const total = `${i18n('assign_provision_assignation')} ${percent} %`

  const min = Math.max(assigned, 1)
  const max = trainingWish.initial_provisions
  const alertMessage = i18n('assign_provision_initial_quantity_message').replace('{min}', min).replace('{max}', max)

  return (
    <div className="grid grid-cols-4 items-center border border-slate-300 rounded-xxs p-4">
      <Cell title={i18n('assign_provision_initial_quantity')}>
        <ConfirmInputNumber
          value={initial}
          setValue={(v: number) => setInitial(v)}
          onConfirm={() => onInitialChange(initial)}
          onCancel={cancel}
          min={min}
          max={max}
          alertMessage={alertMessage}
          cancelOnClickOutside
        />
      </Cell>
      <Cell title={i18n('assign_provision_assigned_quantity')}>
        <span className="quantity assigned">{assigned}</span>
      </Cell>
      <Cell title={i18n('assign_provision_remaining_quantity')}>
        <span className="quantity remaining">{remaining < 0 ? 0 : remaining}</span>
      </Cell>
      <Donut
        title=""
        data={donut}
        total={total}
        pieChartProps={{ data: donut, radius: 45, startAngle: -90, lineWidth: 40 }}
      />
    </div>
  )
}

export default Distribution
